import { Box, Text } from "@chakra-ui/react";
import Tag from "@ui-kit/data-display/Tag/Tag.web";
import { enums } from "@shared/constants";
import { CUSTOMER_REVIEWS } from "@shared/constants/event-detail";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import {
  eventDisplayLowInventory,
  eventHasWaitlistItems,
  isMultiDayEvent,
  isPastEvent,
  isSoldOutEvent,
} from "@shared/modules/events/utils/event-utils";
import { getRecommendedRating } from "@shared/modules/ratings/utils/ratings";
import InventoryBadge from "@ui-application/data-display/badges/InventoryBadge/InventoryBadge.web";
import StatusBadge from "@ui-application/data-display/badges/StatusBadge/StatusBadge.web";
import { useTranslation } from "@shared/utils/internationalization";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { WAFFLE_FLAG_RECOMMENDED_BADGE } from "@shared/constants/waffle-flags";
import Tooltip from "../../overlays/Tooltip/Tooltip.web";

export interface EventStatusesProps {
  event_data?: PublicBaseEventSerializer;
  /** When provided from SSR, used immediately to avoid layout shift while window.waffle loads */
  isRecommendedBadgeEnabled?: boolean;
}

type EventStatus = {
  id: number; // ID is used as a key for React to render and has no real significance
  element: JSX.Element;
  showElement: boolean;
};

const EventStatuses = ({
  event_data,
  isRecommendedBadgeEnabled: ssrRecommendedBadgeEnabled,
}: EventStatusesProps): EventStatus[] => {
  const { t } = useTranslation("purchases");
  const { isFlagActive } = useDjangoWaffle();

  if (!event_data) return [];

  // Check for a null value at the event level, which is the default value. It signifies that the venue value should be inherited.
  const eventThresholdDisplaysEnabled =
    event_data?.venue?.low_inventory_warning_enabled &&
    event_data?.low_inventory_warning_enabled === null;

  // common logic between both inventory badges
  const showBadge =
    eventThresholdDisplaysEnabled &&
    !event_data.external_link &&
    !isSoldOutEvent(event_data) &&
    !isPastEvent(event_data) &&
    !eventHasWaitlistItems(event_data);

  // show "Over X% Sold" badge
  const showLowInventoryBadge =
    showBadge &&
    event_data?.low_ticket_inventory !==
      enums.REMAINING_TICKETS_PERCENTAGE_MAP.NO_WARNING.id &&
    !eventDisplayLowInventory(event_data).displayValue;

  // show "X Tickets Remaining" badge
  const showRemainingTicketsBadge =
    showBadge && eventDisplayLowInventory(event_data).displayValue;
  const isRecommendedBadgeEnabled =
    ssrRecommendedBadgeEnabled ??
    isFlagActive(WAFFLE_FLAG_RECOMMENDED_BADGE, event_data.venue?.id);
  // show "Recommended by fans" badge
  const recommendedRating = getRecommendedRating(event_data.venue?.rating);

  return [
    {
      /* Sold out status */
      id: 0,
      element: <EventStatuses.SoldOut />,
      showElement:
        isSoldOutEvent(event_data) && !eventHasWaitlistItems(event_data),
    },
    {
      /* Display low inventory or remaining tickets badge */
      id: 1,
      element: (
        <InventoryBadge
          eventData={event_data}
          venueData={event_data.venue}
          lowInventoryBadgeProps={{
            hideIcon: false,
            textLengthShort: false,
          }}
        />
      ),
      showElement: showLowInventoryBadge || showRemainingTicketsBadge,
    },
    {
      /* Multiple dates/times status (ie. Recurring events or timeslots) */
      id: 2,
      element: (
        <Box width="fit-content">
          <StatusBadge status="neutral">
            <Text fontSize="xs" textTransform="uppercase">
              {isMultiDayEvent(event_data)
                ? t("Multiple Dates")
                : t("Multiple Times")}
            </Text>
          </StatusBadge>
        </Box>
      ),
      showElement:
        (event_data?.is_recurring_parent && event_data?.child_count > 1) ||
        Boolean(event_data?.attraction_event_config),
    },
    {
      /* Waitlist status */
      id: 3,
      element: (
        <Box width="fit-content">
          <Tag
            fontSize="sm"
            fontWeight="bold"
            backgroundColor="primary.100"
            color="primary.600"
          >
            <Text fontSize="xs" textTransform="uppercase">
              {t("Waitlist")}
            </Text>
          </Tag>
        </Box>
      ),
      showElement:
        isSoldOutEvent(event_data) && eventHasWaitlistItems(event_data),
    },
    {
      /* Recommended status */
      id: 4,
      element: (
        <Tooltip
          label={t(CUSTOMER_REVIEWS.USER_RATING_FOUR_OR_MORE, {
            percentage: recommendedRating.recommendedPercentage,
          })}
          hasArrow
        >
          <Tag
            fontSize="sm"
            fontWeight="bold"
            backgroundColor="pink.100"
            border="none"
            color="pink.600"
          >
            <Text fontSize="xs" textTransform="uppercase">
              {t(CUSTOMER_REVIEWS.RECOMMENDED_BY_FANS, {
                percentage: recommendedRating.recommendedPercentage,
              })}
            </Text>
          </Tag>
        </Tooltip>
      ),
      showElement: recommendedRating.isRecommended && isRecommendedBadgeEnabled,
    },
  ];
};

export default EventStatuses;

EventStatuses.SoldOut = function SoldOut() {
  const { t } = useTranslation("purchases");

  return (
    <Box width="fit-content">
      <StatusBadge status="error">
        <Text fontSize="xs" textTransform="uppercase">
          {t("Sold Out")}
        </Text>
      </StatusBadge>
    </Box>
  );
};
